import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Button,
    Select,
    DatePicker,
    Badge,
    Separator,
} from '@shared/components';
import { FilterX } from 'lucide-react';
import type { DashboardFilters } from '../../types/filters.types';
import {
    useDealersQuery,
    useProductsQuery,
    useColleaguesQuery,
} from '../../hooks/useDashboardFilters';
import { RECORD_TYPE_OPTIONS, STATUS_OPTIONS } from '../../types/filters.types';

interface DashboardFiltersProps {
    filters: DashboardFilters;
    onFiltersChange: (filters: DashboardFilters) => void;
    onClearFilters: () => void;
}

const selectUserId = (state: RootState) => state.auth.user?.id;

export const Filters: React.FC<DashboardFiltersProps> = ({
    filters,
    onFiltersChange,
    onClearFilters,
}) => {
    // Get userId from Redux
    const userId = useSelector(selectUserId);

    const dealerQuery = useDealersQuery(userId ?? '');
    const productQuery = useProductsQuery(userId ?? '');
    const dealersFetched = dealerQuery.isFetchedAfterMount;
    const productsFetched = productQuery.isFetchedAfterMount;
    const dealers = dealersFetched && !dealerQuery.isError ? (dealerQuery.data ?? []) : [];
    const products = productsFetched && !productQuery.isError ? (productQuery.data ?? []) : [];
    const dealersLoading = !dealersFetched;
    const productsLoading = !productsFetched;
    const { data: colleagues = [], isLoading: colleaguesLoading } = useColleaguesQuery(userId ?? '');

    const updateFilter = (key: keyof DashboardFilters, value: DashboardFilters[keyof DashboardFilters]) => {
        onFiltersChange({
            ...filters,
            [key]: value,
        });
    };

    const getActiveFiltersCount = () => {
        return Object.values(filters).filter(
            (value) =>
                value !== undefined &&
                value !== null &&
                (Array.isArray(value) ? value.length > 0 : true)
        ).length;
    };

    // Convert your filter options to the format expected by your Select component
    const recordTypeOptions = [{ value: '', label: 'All types' }, ...RECORD_TYPE_OPTIONS];

    const statusOptions = [{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS];

    const dealerOptions = [
        { value: '', label: 'All dealers' },
        ...dealers.map((dealer) => ({
            value: dealer.value,
            label: dealer.code ? `${dealer.label} (${dealer.code})` : dealer.label,
        })),
    ];

    const productOptions = [{ value: '', label: 'All products' }, ...products];

    const colleagueOptions = [{ value: '', label: 'All salespeople' }, ...colleagues];

    // Helper functions for date handling
    const handleDateFromChange = (date: Date | null) => {
        if (date) {
            // Convert to YYYY-MM-DD format for the API
            const isoString = date.toISOString().split('T')[0];
            updateFilter('date_from', isoString);
        } else {
            updateFilter('date_from', undefined);
        }
    };

    const handleDateToChange = (date: Date | null) => {
        if (date) {
            // Convert to YYYY-MM-DD format for the API
            const isoString = date.toISOString().split('T')[0];
            updateFilter('date_to', isoString);
        } else {
            updateFilter('date_to', undefined);
        }
    };

    // Convert date strings back to Date objects for the DatePicker
    const getDateFromValue = () => {
        return filters.date_from ? new Date(filters.date_from) : undefined;
    };

    const getDateToValue = () => {
        return filters.date_to ? new Date(filters.date_to) : undefined;
    };

    return (
        <Card className="mb-6">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">Filters</CardTitle>
                    <div className="flex items-center gap-2">
                        {getActiveFiltersCount() > 0 && (
                            <Badge variant="info">{getActiveFiltersCount()} active</Badge>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onClearFilters}
                            className="h-8"
                        >
                            <FilterX className="h-4 w-4 mr-1" />
                            Clear
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* First Row: Record Type & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Record Type */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Record Type</label>
                        </div>
                        <Select
                            options={recordTypeOptions}
                            value={filters.record_type || ''}
                            onChange={(value) => updateFilter('record_type', value || undefined)}
                            placeholder="All types"
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Status</label>
                        </div>
                        <Select
                            options={statusOptions}
                            value={filters.status?.[0] || ''}
                            onChange={(value) =>
                                updateFilter('status', value ? [value] : undefined)
                            }
                            placeholder="All statuses"
                        />
                    </div>
                </div>

                <Separator />

                {/* Second Row: Dealers & Products */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Dealers */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Dealers</label>
                        </div>
                        <Select
                            options={dealerOptions}
                            value={filters.dealer_id?.[0] || ''}
                            onChange={(value) =>
                                updateFilter('dealer_id', value ? [value] : undefined)
                            }
                            placeholder={dealersLoading ? 'Loading...' : 'All dealers'}
                            disabled={dealersLoading}
                            isLoading={dealersLoading}
                        />
                    </div>

                    {/* Products */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Products</label>
                        </div>
                        <Select
                            options={productOptions}
                            value={filters.product_id?.[0] || ''}
                            onChange={(value) =>
                                updateFilter('product_id', value ? [value] : undefined)
                            }
                            placeholder={productsLoading ? 'Loading...' : 'All products'}
                            disabled={productsLoading}
                            isLoading={productsLoading}
                        />
                    </div>
                </div>

                <Separator />

                {/* Third Row: Salesperson & Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Salesperson */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Salesperson</label>
                        </div>
                        <Select
                            options={colleagueOptions}
                            value={filters.salesperson_id || ''}
                            onChange={(value) =>
                                updateFilter('salesperson_id', value ? value : undefined)
                            }
                            placeholder={colleaguesLoading ? 'Loading...' : 'All salespeople'}
                            disabled={colleaguesLoading}
                            isLoading={colleaguesLoading}
                        />
                    </div>

                    {/* Date From */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Date From</label>
                        </div>
                        <DatePicker
                            id="date-from-filter"
                            mode="single"
                            onChange={handleDateFromChange}
                            defaultDate={getDateFromValue()}
                            placeholder="Select start date"
                            maxDate={filters.date_to ? new Date(filters.date_to) : undefined}
                        />
                    </div>

                    {/* Date To */}
                    <div className="space-y-2">
                        <div className="flex gap-1">
                            <label className="text-sm font-medium">Date To</label>
                        </div>
                        <DatePicker
                            id="date-to-filter"
                            mode="single"
                            onChange={handleDateToChange}
                            defaultDate={getDateToValue()}
                            placeholder="Select end date"
                            minDate={filters.date_from ? new Date(filters.date_from) : undefined}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
